import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Spinner } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import { getClients } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

export default function Clients() {
  const [clients, setClients] = useState(null);
  useEffect(() => { getClients().then(setClients).catch(() => setClients([])); }, []);

  return (
    <div data-testid="clients-page">
      <SEO
        title="Programs & Companies — What Julius Mason Creates and Supports"
        description="Programs, products and companies created or supported by Julius Mason — his own ventures and the teams he helps build."
        path="/clients"
        lang="en"
        keywords="julius mason programs, julius mason companies, ventures, AVENO"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Programs", path: "/clients" },
        ])}
      />
      <PageHeader
        chapter="Programs / Companies"
        titleLines={["Things I've created.", "Teams I support."]}
        description="Programs, products and companies I've built or backed — my own ventures and the teams I help grow along the way."
      />
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          {!clients ? <Spinner /> : clients.length === 0 ? (
            <div className="max-w-xl" data-testid="programs-empty">
              <p className="text-muted-foreground leading-relaxed mb-8">
                New programs and companies are being added soon — including AVENO, my everyday-goods brand.
                Want yours to be next?
              </p>
              <Link to="/start-project" data-testid="programs-cta"
                className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-7 py-3.5 rounded-full hover:bg-primary/90 active:scale-95 transition">
                Start a project <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((c, i) => (
                <Reveal key={c.id} delay={(i % 3) * 0.06}>
                  <div data-testid={`client-card-${c.id}`} className="group border border-border p-8 hover:bg-secondary/40 transition-colors h-full flex flex-col">
                    {c.logo && (
                      <div className="aspect-video overflow-hidden border border-border mb-6">
                        <img src={c.logo} alt={c.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                      </div>
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-primary border border-primary/40 px-2 py-1 self-start mb-3">{c.role || "Program"}</span>
                    <h3 className="font-display text-2xl">{c.name}</h3>
                    <p className="font-mono text-xs text-primary uppercase tracking-widest mt-1">{c.industry}</p>
                    {c.services?.length > 0 && (
                      <p className="text-sm text-muted-foreground mt-4">{(c.services || []).join(", ")}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
